import { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { Calendar as CalendarIcon, ChevronLeft, ChevronRight, Plus, Mic } from 'lucide-react';
import { MESI, formatDateLocal, parseVoiceText } from '../lib/calendarioUtils';
import CalendarioViste from '../components/calendario/CalendarioViste';
import AppuntamentoDialog from '../components/calendario/AppuntamentoDialog';
import VoiceDialog from '../components/calendario/VoiceDialog';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function CalendarioPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [vista, setVista] = useState('mese'); // 'giorno', 'settimana', 'mese'
  const [dataCorrente, setDataCorrente] = useState(new Date());
  const [appuntamenti, setAppuntamenti] = useState([]);
  const [tecnici, setTecnici] = useState([]);
  const [clienti, setClienti] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Dialog
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogMode, setDialogMode] = useState('create'); // 'create', 'edit', 'view'
  const [selectedAppuntamento, setSelectedAppuntamento] = useState(null);
  const [formData, setFormData] = useState({
    titolo: '',
    descrizione: '',
    data: '',
    ora_inizio: '09:00',
    ora_fine: '10:00',
    tecnico_id: '',
    cliente_id: '',
    luogo: '',
    nome_contatto: '',
    telefono_contatto: '',
    note: ''
  });
  
  // Ricerca cliente
  const [clienteSearch, setClienteSearch] = useState('');
  const [clienteSearchResults, setClienteSearchResults] = useState([]);
  const [selectedCliente, setSelectedCliente] = useState(null);
  
  // Voice input
  const [isListening, setIsListening] = useState(false);
  const [voiceText, setVoiceText] = useState('');
  const [voiceDialogOpen, setVoiceDialogOpen] = useState(false);
  const [voiceParsing, setVoiceParsing] = useState(false);
  const [voiceParsedData, setVoiceParsedData] = useState(null);
  const recognitionRef = useRef(null);
  
  // Flag per sapere se abbiamo già processato i parametri URL (da ticket)
  const [urlParamsProcessed, setUrlParamsProcessed] = useState(false);

  useEffect(() => {
    fetchTecniciClienti();
  }, []);
  
  // Gestione parametri URL da ticket - apri il dialog precompilato
  useEffect(() => {
    if (urlParamsProcessed || !clienti.length || !tecnici.length) return;
    
    const ticketId = searchParams.get('ticket_id');
    const ticketNumero = searchParams.get('ticket_numero');
    const clienteId = searchParams.get('cliente_id');
    const clienteNome = searchParams.get('cliente_nome');
    const oggetto = searchParams.get('oggetto');
    const tecnicoId = searchParams.get('tecnico_id');
    
    if (ticketId || clienteId) {
      // Trova il cliente se presente
      let clienteObj = null;
      let luogo = '';
      if (clienteId) {
        clienteObj = clienti.find(c => c.id === clienteId);
        if (clienteObj) {
          if (clienteObj.indirizzo_legale) {
            luogo = clienteObj.indirizzo_legale;
            if (clienteObj.cap_legale) luogo += `, ${clienteObj.cap_legale}`;
            if (clienteObj.citta_legale) luogo += ` ${clienteObj.citta_legale}`;
          }
        }
      }
      
      // Prepara titolo
      const titolo = ticketNumero 
        ? `Ticket #${ticketNumero}${clienteNome ? ` - ${clienteNome}` : ''}`
        : oggetto || (clienteNome ? `Appuntamento ${clienteNome}` : 'Nuovo appuntamento');
      
      // Prepara descrizione/note
      const note = oggetto ? `Ticket: ${oggetto}` : '';
      
      // Imposta form data
      setFormData({
        titolo: titolo,
        descrizione: '',
        data: formatDateLocal(new Date()),
        ora_inizio: '09:00',
        ora_fine: '10:00',
        tecnico_id: tecnicoId || '',
        cliente_id: clienteId || '',
        luogo: luogo,
        nome_contatto: '',
        telefono_contatto: '',
        note: note
      });
      
      // Imposta il cliente selezionato
      if (clienteObj) {
        setSelectedCliente(clienteObj);
        setClienteSearch(clienteObj.ragione_sociale);
      }
      
      // Apri il dialog
      setDialogMode('create');
      setDialogOpen(true);
      
      // Pulisci i parametri URL per evitare che riapri il dialog al refresh
      setSearchParams({});
      setUrlParamsProcessed(true);
      
      toast.info(`Creazione appuntamento per Ticket #${ticketNumero || 'N/A'}`);
    }
  }, [searchParams, clienti, tecnici, urlParamsProcessed, setSearchParams]);

  const fetchTecniciClienti = async () => {
    try {
      const [tecniciRes, clientiRes] = await Promise.all([
        axios.get(`${API}/tecnici`),
        axios.get(`${API}/clienti`)
      ]);
      setTecnici(tecniciRes.data);
      setClienti(clientiRes.data);
    } catch (error) {
      console.error('Error loading data:', error);
    }
  };

  const fetchAppuntamenti = useCallback(async () => {
    setLoading(true);
    try {
      let dataInizio, dataFine;
      
      if (vista === 'mese') {
        const primoGiorno = new Date(dataCorrente.getFullYear(), dataCorrente.getMonth(), 1);
        const ultimoGiorno = new Date(dataCorrente.getFullYear(), dataCorrente.getMonth() + 1, 0);
        // Estendi per mostrare giorni del mese precedente/successivo visibili
        primoGiorno.setDate(primoGiorno.getDate() - primoGiorno.getDay() + 1);
        ultimoGiorno.setDate(ultimoGiorno.getDate() + (7 - ultimoGiorno.getDay()));
        dataInizio = formatDateLocal(primoGiorno);
        dataFine = formatDateLocal(ultimoGiorno);
      } else if (vista === 'settimana') {
        const inizioSettimana = new Date(dataCorrente);
        inizioSettimana.setDate(dataCorrente.getDate() - dataCorrente.getDay() + 1);
        const fineSettimana = new Date(inizioSettimana);
        fineSettimana.setDate(inizioSettimana.getDate() + 6);
        dataInizio = formatDateLocal(inizioSettimana);
        dataFine = formatDateLocal(fineSettimana);
      } else {
        dataInizio = formatDateLocal(dataCorrente);
        dataFine = dataInizio;
      }
      
      const response = await axios.get(`${API}/appuntamenti?data_inizio=${dataInizio}&data_fine=${dataFine}`);
      setAppuntamenti(response.data);
    } catch (error) {
      console.error('Error loading appuntamenti:', error);
    } finally {
      setLoading(false);
    }
  }, [dataCorrente, vista]);

  useEffect(() => {
    fetchAppuntamenti();
  }, [fetchAppuntamenti]);

  const navigaAvanti = () => {
    const nuovaData = new Date(dataCorrente);
    if (vista === 'mese') nuovaData.setMonth(nuovaData.getMonth() + 1);
    else if (vista === 'settimana') nuovaData.setDate(nuovaData.getDate() + 7);
    else nuovaData.setDate(nuovaData.getDate() + 1);
    setDataCorrente(nuovaData);
  };

  const navigaIndietro = () => {
    const nuovaData = new Date(dataCorrente);
    if (vista === 'mese') nuovaData.setMonth(nuovaData.getMonth() - 1);
    else if (vista === 'settimana') nuovaData.setDate(nuovaData.getDate() - 7);
    else nuovaData.setDate(nuovaData.getDate() - 1);
    setDataCorrente(nuovaData);
  };

  const vaiOggi = () => setDataCorrente(new Date());

  // Ricerca cliente per nome o P.IVA
  const handleClienteSearch = (value) => {
    setClienteSearch(value);
    if (value.length >= 2) {
      const searchLower = value.toLowerCase();
      const results = clienti.filter(c => 
        c.ragione_sociale?.toLowerCase().includes(searchLower) ||
        c.partita_iva?.includes(value)
      ).slice(0, 10);
      setClienteSearchResults(results);
    } else {
      setClienteSearchResults([]);
    }
  };

  // Seleziona cliente e auto-compila indirizzo
  const handleSelectCliente = (cliente) => {
    setSelectedCliente(cliente);
    setClienteSearch(cliente.ragione_sociale);
    setClienteSearchResults([]);
    
    // Costruisci indirizzo completo dal cliente
    let indirizzo = '';
    if (cliente.indirizzo_legale) {
      indirizzo = cliente.indirizzo_legale;
      if (cliente.cap_legale) indirizzo += `, ${cliente.cap_legale}`;
      if (cliente.citta_legale) indirizzo += ` ${cliente.citta_legale}`;
      if (cliente.provincia_legale) indirizzo += ` (${cliente.provincia_legale})`;
    }
    
    setFormData({
      ...formData, 
      cliente_id: cliente.id,
      luogo: indirizzo || formData.luogo
    });
  };

  // Reset ricerca cliente
  const resetClienteSearch = () => {
    setClienteSearch('');
    setClienteSearchResults([]);
    setSelectedCliente(null);
    setFormData({...formData, cliente_id: '', luogo: ''});
  };

  // ===== VOICE INPUT FUNCTIONS =====
  
  // Inizia ascolto vocale
  const startVoiceInput = () => {
    if (!('webkitSpeechRecognition' in window) && !('SpeechRecognition' in window)) {
      toast.error('Il tuo browser non supporta il riconoscimento vocale. Usa Chrome o Edge.');
      return;
    }
    
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    recognitionRef.current = new SpeechRecognition();
    recognitionRef.current.lang = 'it-IT';
    recognitionRef.current.continuous = true;
    recognitionRef.current.interimResults = true;
    
    recognitionRef.current.onstart = () => {
      setIsListening(true);
      setVoiceText('');
      setVoiceDialogOpen(true);
      toast.info('🎤 Parla ora... Es: "Appuntamento per Mario da Ristorante Bella Vista domani alle 10"');
    };
    
    recognitionRef.current.onresult = (event) => {
      let transcript = '';
      for (let i = 0; i < event.results.length; i++) {
        transcript += event.results[i][0].transcript;
      }
      setVoiceText(transcript);
    };
    
    recognitionRef.current.onerror = (event) => {
      console.error('Speech recognition error:', event.error);
      if (event.error !== 'no-speech') {
        toast.error('Errore nel riconoscimento vocale');
      }
      setIsListening(false);
    };
    
    recognitionRef.current.onend = () => {
      setIsListening(false);
    };
    
    recognitionRef.current.start();
  };
  
  // Ferma ascolto e processa
  const stopVoiceInput = () => {
    if (recognitionRef.current) {
      recognitionRef.current.stop();
    }
    setIsListening(false);
    
    if (voiceText.trim()) {
      parseVoiceInput(voiceText);
    }
  };
  
  // Analizza il testo vocale (parser estratto in lib/calendarioUtils)
  const parseVoiceInput = (text) => {
    setVoiceParsing(true);
    setVoiceParsedData(parseVoiceText(text, tecnici, clienti));
    setVoiceParsing(false);
  };
  
  // Conferma appuntamento vocale
  const confirmVoiceAppointment = () => {
    if (!voiceParsedData) return;
    
    // Prepara i dati per il form
    setFormData({
      titolo: voiceParsedData.titolo,
      descrizione: voiceParsedData.testoOriginale,
      data: voiceParsedData.data,
      ora_inizio: voiceParsedData.ora_inizio,
      ora_fine: voiceParsedData.ora_fine,
      tecnico_id: voiceParsedData.tecnico?.id || '',
      cliente_id: voiceParsedData.cliente?.id || '',
      luogo: voiceParsedData.luogo,
      nome_contatto: '',
      telefono_contatto: '',
      note: ''
    });
    
    if (voiceParsedData.cliente) {
      setSelectedCliente(voiceParsedData.cliente);
      setClienteSearch(voiceParsedData.cliente.ragione_sociale);
    } else {
      setSelectedCliente(null);
      setClienteSearch('');
    }
    
    // Chiudi dialog voce e apri form
    setVoiceDialogOpen(false);
    setVoiceParsedData(null);
    setVoiceText('');
    setDialogMode('create');
    setDialogOpen(true);
  };
  
  // Annulla input vocale
  const cancelVoiceInput = () => {
    if (recognitionRef.current) {
      recognitionRef.current.stop();
    }
    setIsListening(false);
    setVoiceDialogOpen(false);
    setVoiceText('');
    setVoiceParsedData(null);
  };

  // ===== END VOICE INPUT FUNCTIONS =====

  const apriNuovoAppuntamento = (data = null) => {
    setDialogMode('create');
    setSelectedAppuntamento(null);
    setClienteSearch('');
    setClienteSearchResults([]);
    setSelectedCliente(null);
    setFormData({
      titolo: '',
      descrizione: '',
      data: data || formatDateLocal(dataCorrente),
      ora_inizio: '09:00',
      ora_fine: '10:00',
      tecnico_id: '',
      cliente_id: '',
      luogo: '',
      nome_contatto: '',
      telefono_contatto: '',
      note: ''
    });
    setDialogOpen(true);
  };

  const apriModifica = (app) => {
    setDialogMode('edit');
    setSelectedAppuntamento(app);
    
    // Se c'è un cliente, caricalo per la ricerca
    if (app.cliente_id) {
      const cliente = clienti.find(c => c.id === app.cliente_id);
      if (cliente) {
        setSelectedCliente(cliente);
        setClienteSearch(cliente.ragione_sociale);
      }
    } else {
      setSelectedCliente(null);
      setClienteSearch('');
    }
    setClienteSearchResults([]);
    
    setFormData({
      titolo: app.titolo,
      descrizione: app.descrizione || '',
      data: app.data,
      ora_inizio: app.ora_inizio,
      ora_fine: app.ora_fine || '',
      tecnico_id: app.tecnico_id || '',
      cliente_id: app.cliente_id || '',
      luogo: app.luogo || '',
      nome_contatto: app.nome_contatto || '',
      telefono_contatto: app.telefono_contatto || '',
      note: app.note || ''
    });
    setDialogOpen(true);
  };

  const apriDettaglio = (app) => {
    setDialogMode('view');
    setSelectedAppuntamento(app);
    setDialogOpen(true);
  };

  const handleSubmit = async () => {
    if (!formData.titolo || !formData.data || !formData.ora_inizio) {
      toast.error('Compila i campi obbligatori: Titolo, Data, Ora');
      return;
    }

    try {
      if (dialogMode === 'create') {
        await axios.post(`${API}/appuntamenti`, formData);
        toast.success('Appuntamento creato');
      } else {
        await axios.put(`${API}/appuntamenti/${selectedAppuntamento.id}`, formData);
        toast.success('Appuntamento aggiornato');
      }
      setDialogOpen(false);
      fetchAppuntamenti();
    } catch (error) {
      toast.error('Errore nel salvataggio');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Eliminare questo appuntamento?')) return;
    try {
      await axios.delete(`${API}/appuntamenti/${id}`);
      toast.success('Appuntamento eliminato');
      setDialogOpen(false);
      fetchAppuntamenti();
    } catch (error) {
      toast.error('Errore nell\'eliminazione');
    }
  };

  // Genera griglia mese
  const giorniMese = useMemo(() => {
    const giorni = [];
    const primoGiorno = new Date(dataCorrente.getFullYear(), dataCorrente.getMonth(), 1);
    const ultimoGiorno = new Date(dataCorrente.getFullYear(), dataCorrente.getMonth() + 1, 0);
    
    // Inizia dal lunedì della settimana del primo giorno
    const inizio = new Date(primoGiorno);
    inizio.setDate(inizio.getDate() - ((inizio.getDay() + 6) % 7));
    
    // Genera 6 settimane (42 giorni)
    for (let i = 0; i < 42; i++) {
      const giorno = new Date(inizio);
      giorno.setDate(inizio.getDate() + i);
      giorni.push(giorno);
    }
    return giorni;
  }, [dataCorrente]);

  // Genera griglia settimana
  const giorniSettimana = useMemo(() => {
    const giorni = [];
    const inizio = new Date(dataCorrente);
    inizio.setDate(dataCorrente.getDate() - ((dataCorrente.getDay() + 6) % 7));
    for (let i = 0; i < 7; i++) {
      const giorno = new Date(inizio);
      giorno.setDate(inizio.getDate() + i);
      giorni.push(giorno);
    }
    return giorni;
  }, [dataCorrente]);

  // Ottieni appuntamenti per data
  const getAppuntamentiGiorno = (data) => {
    const dataStr = formatDateLocal(data);
    return appuntamenti.filter(a => a.data === dataStr);
  };

  const getTitoloVista = () => {
    if (vista === 'mese') return `${MESI[dataCorrente.getMonth()]} ${dataCorrente.getFullYear()}`;
    if (vista === 'settimana') {
      const inizio = new Date(giorniSettimana[0]);
      const fine = new Date(giorniSettimana[6]);
      return `${inizio.getDate()} - ${fine.getDate()} ${MESI[fine.getMonth()]} ${fine.getFullYear()}`;
    }
    return `${dataCorrente.getDate()} ${MESI[dataCorrente.getMonth()]} ${dataCorrente.getFullYear()}`;
  };

  return (
    <div className="space-y-4 animate-fade-in" data-testid="calendario-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-black tracking-tight flex items-center gap-3">
            <CalendarIcon className="h-8 w-8 text-cyan-500" />
            Calendario
          </h1>
        </div>
        <div className="flex gap-2">
          <Button 
            onClick={startVoiceInput} 
            variant="outline"
            className="rounded-none border-cyan-500 text-cyan-600 hover:bg-cyan-50 dark:hover:bg-cyan-950" 
            data-testid="voice-appuntamento-btn"
          >
            <Mic className="h-4 w-4 mr-2" />
            Voce
          </Button>
          <Button onClick={() => apriNuovoAppuntamento()} className="rounded-none" data-testid="new-appuntamento-btn">
            <Plus className="h-4 w-4 mr-2" />
            Nuovo Appuntamento
          </Button>
        </div>
      </div>

      {/* Controlli */}
      <Card className="rounded-none">
        <CardContent className="p-4">
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div className="flex items-center gap-2">
              <Button variant="outline" size="icon" onClick={navigaIndietro} className="rounded-none">
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <Button variant="outline" onClick={vaiOggi} className="rounded-none text-xs font-mono">
                OGGI
              </Button>
              <Button variant="outline" size="icon" onClick={navigaAvanti} className="rounded-none">
                <ChevronRight className="h-4 w-4" />
              </Button>
              <span className="font-bold text-lg ml-4">{getTitoloVista()}</span>
            </div>
            
            <div className="flex gap-1">
              {['giorno', 'settimana', 'mese'].map((v) => (
                <Button
                  key={v}
                  variant={vista === v ? 'default' : 'outline'}
                  onClick={() => setVista(v)}
                  className="rounded-none text-xs font-mono uppercase"
                  data-testid={`vista-${v}`}
                >
                  {v}
                </Button>
              ))}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Legenda Tecnici */}
      <div className="flex flex-wrap gap-2">
        {tecnici.map((t) => (
          <Badge 
            key={t.id} 
            variant="outline" 
            className="rounded-none text-xs"
            style={{ borderColor: t.colore || '#3b82f6', color: t.colore || '#3b82f6' }}
          >
            <span className="w-2 h-2 rounded-full mr-2" style={{ backgroundColor: t.colore || '#3b82f6' }}></span>
            {t.nome} {t.cognome}
          </Badge>
        ))}
      </div>

      <CalendarioViste
        vista={vista}
        dataCorrente={dataCorrente}
        giorniMese={giorniMese}
        giorniSettimana={giorniSettimana}
        getAppuntamentiGiorno={getAppuntamentiGiorno}
        onNuovo={apriNuovoAppuntamento}
        onDettaglio={apriDettaglio}
      />

      <AppuntamentoDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        dialogMode={dialogMode}
        selectedAppuntamento={selectedAppuntamento}
        formData={formData}
        setFormData={setFormData}
        tecnici={tecnici}
        clienteSearch={clienteSearch}
        clienteSearchResults={clienteSearchResults}
        selectedCliente={selectedCliente}
        onClienteSearch={handleClienteSearch}
        onSelectCliente={handleSelectCliente}
        onResetCliente={resetClienteSearch}
        onSubmit={handleSubmit}
        onDelete={handleDelete}
        onEdit={apriModifica}
      />

      <VoiceDialog
        open={voiceDialogOpen}
        isListening={isListening}
        voiceText={voiceText}
        setVoiceText={setVoiceText}
        voiceParsing={voiceParsing}
        voiceParsedData={voiceParsedData}
        setVoiceParsedData={setVoiceParsedData}
        onStart={startVoiceInput}
        onStop={stopVoiceInput}
        onParse={parseVoiceInput}
        onConfirm={confirmVoiceAppointment}
        onCancel={cancelVoiceInput}
      />
    </div>
  );
}
